import { DocumentNode, gql } from '@apollo/client';
import { Button, ButtonProps, useDisclosure } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { event_registration_clicked } from '@/analytics';
import { useCurrent } from '@/hooks/useCurrent';
import { usePermissions } from '@/hooks/usePermissions';
import { shouldUseMemberLearningRegistrationCopy } from '@/utilities/memberLearningRegistrationCopy';

import { TicketedEventRegistrationButton_EventFragment } from './graphql-types/TicketedEventRegistrationButton_EventFragment';

import { AddToCartModal } from './components/EventRegistrationModal';
import { ExternalRegistrationModal } from './components/ExternalRegistrationModal';

export const TicketedEventRegistrationButton: React.FC<
  {
    event: TicketedEventRegistrationButton_EventFragment;
  } & ButtonProps
> & {
  eventFragment: DocumentNode;
} = ({ event, children, ...props }) => {
  const router = useRouter();

  const rModalOpen = router.query.rModalOpen
    ? router.query.rModalOpen.toString()
    : undefined;

  const ticketLinkId = router.query.ticketLinkId
    ? router.query.ticketLinkId.toString()
    : undefined;

  const preSelectedTicketId = router.query.preSelectedTicketId
    ? router.query.preSelectedTicketId.toString()
    : undefined;

  const permissions = usePermissions();

  const registrationModalDisclosure = useDisclosure({
    defaultIsOpen: !!rModalOpen || !!ticketLinkId || !!preSelectedTicketId,
  });

  const current = useCurrent();
  const useLearningRegistrationCopy =
    shouldUseMemberLearningRegistrationCopy(event);
  const analyticsEventType = event.isCourseWorkshop
    ? 'course_workshop'
    : event.__typename === 'ConferenceEventV2'
    ? 'conference'
    : 'simple';
  const analyticsEntityType = event.isCourseWorkshop
    ? 'course_workshop'
    : event.__typename === 'ConferenceEventV2'
    ? 'conference'
    : 'simple_event';
  const eventStartDate = event.startDate ?? new Date().toISOString();
  const eventEndDate = event.endDate ?? eventStartDate;

  React.useEffect(() => {
    if (router.isReady && !!rModalOpen) {
      // Event user journey: member opened ticketed register modal from a deep link
      event_registration_clicked({
        entity_id: event._id,
        entity_type: analyticsEntityType,
        event_name: event.name,
        event_type: analyticsEventType,
        event_start_date: new Date(eventStartDate).toISOString(),
        ticket_options:
          event.ticketsCurrentUserCanPurchase?.map(({ _id }) => _id) ?? [],
        learning_provider: event.isCourseWorkshop ? 'intuto' : undefined,
      });
      // Remove rModalOpen from URL but preserve other parameters like ticketLinkId
      const { rModalOpen, ...query } = router.query;
      router.replace({ query }, undefined, { shallow: true });
    }
  }, [
    analyticsEntityType,
    analyticsEventType,
    event,
    router,
    current.user?._id,
    rModalOpen,
    eventStartDate,
  ]);

  return permissions.isLoading ? null : (
    <>
      <Button
        onClick={() => {
          // Event user journey: member clicked Register on a ticketed simple/conference event
          event_registration_clicked({
            entity_id: event._id,
            entity_type: analyticsEntityType,
            event_name: event.name,
            event_type: analyticsEventType,
            event_start_date: eventStartDate,
            ticket_options:
              event.ticketsCurrentUserCanPurchase?.map(({ _id }) => _id) ?? [],
            learning_provider: event.isCourseWorkshop ? 'intuto' : undefined,
          });

          registrationModalDisclosure.onOpen();
        }}
        {...props}
      >
        {children
          ? children
          : useLearningRegistrationCopy
          ? 'Enroll'
          : 'Register'}
      </Button>
      {event.isExternalRegistrationUrlActive &&
      event.externalRegistrationUrl ? (
        <ExternalRegistrationModal
          isOpen={registrationModalDisclosure.isOpen}
          onClose={registrationModalDisclosure.onClose}
          externalRegistrationUrl={event.externalRegistrationUrl}
          useLearningRegistrationCopy={useLearningRegistrationCopy}
        />
      ) : (
        <AddToCartModal
          isOpen={registrationModalDisclosure.isOpen}
          onClose={registrationModalDisclosure.onClose}
          eventId={event._id}
          eventName={event.name}
          eventStartDate={eventStartDate}
          eventEndDate={eventEndDate}
          isConference={event.__typename === 'ConferenceEventV2'}
        />
      )}
    </>
  );
};

TicketedEventRegistrationButton.eventFragment = gql`
  fragment TicketedEventRegistrationButton_EventFragment on EventV2 {
    _id
    name
    isCourseWorkshop
    isExternalRegistrationUrlActive
    externalRegistrationUrl
    ticketsCurrentUserCanPurchase {
      _id
    }
    startDate
    endDate
    ... on SimpleEventV2 {
      intutoCourseCollection {
        collectionId
      }
    }
    ... on ConferenceEventV2 {
      intutoCourseCollection {
        collectionId
      }
    }
  }
`;
